import { z } from "zod";

/**
 * The campaign tags a visitor arrived with: which ad, in plain words.
 *
 * Adkiro's own ads link to the site with utm_ tags, and until 3 Oct 2026
 * nothing read them. The first real enquiry came by WhatsApp on 2 Oct, two
 * people had clicked two different ads that morning, and there was no way to
 * say which one had worked.
 *
 * The tags are read once from the landing address and held in memory only.
 * Nothing is written to the device, so there is nothing to ask consent for,
 * and a reload forgets them. That is the right trade here: the visitors who
 * matter enquire in the visit they arrived in.
 */
const tag = z
  .string()
  .trim()
  .max(80)
  // Tags end up in an alert and a database row; keep them to what a tag is.
  .regex(/^[\w .+-]*$/)
  .optional();

export const campaignSchema = z.object({
  source: tag,
  medium: tag,
  campaign: tag,
  content: tag,
});

export type CampaignTags = z.infer<typeof campaignSchema>;

let landed: CampaignTags | undefined;

/** The tags from the address this visit landed on. Empty on the server. */
export function campaignTags(): CampaignTags {
  if (landed) return landed;
  if (typeof window === "undefined") return {};
  const query = new URLSearchParams(window.location.search);
  const parsed = campaignSchema.safeParse({
    source: query.get("utm_source") ?? undefined,
    medium: query.get("utm_medium") ?? undefined,
    campaign: query.get("utm_campaign") ?? undefined,
    content: query.get("utm_content") ?? undefined,
  });
  // A malformed tag is dropped whole rather than passed on half-cleaned.
  landed = parsed.success ? parsed.data : {};
  return landed;
}

/** One line for an alert: the ad if it is known, and an honest "unknown" if not. */
export function describeCampaign(tags: CampaignTags): string {
  if (!tags.content && !tags.campaign && !tags.source) {
    return "No campaign tag (a search, a direct visit or a link without tags)";
  }
  const where = [tags.source, tags.campaign].filter(Boolean).join(" / ");
  return tags.content ? `Ad "${tags.content}"${where ? ` (${where})` : ""}` : where;
}
